import React, { useState, useRef, useEffect, useCallback } from 'react';
import SelectField from './SelectField';
import { createPortal } from 'react-dom';
import { DateTime } from 'luxon';
import {
  FaRegCalendarAlt,
  FaRegClock,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';

// Themed drop-in replacement for native <input type="date|datetime-local|time">.
// The native popup can't be branded (browser-drawn blue highlights), so the
// popup here is app-rendered: a portaled, viewport-clamped panel styled with
// the app's cloudza palette. The visible field stays a real text input so
// existing fireEvent.change-based tests and `e.target.value` handlers work
// unchanged.
//
// Value formats match the native inputs exactly:
//   mode="date"     -> YYYY-MM-DD
//   mode="datetime" -> YYYY-MM-DDTHH:mm
//   mode="time"     -> HH:mm

const FORMATS = {
  date: 'yyyy-MM-dd',
  datetime: "yyyy-MM-dd'T'HH:mm",
  time: 'HH:mm',
};

const PLACEHOLDERS = {
  date: 'YYYY-MM-DD',
  datetime: 'YYYY-MM-DD HH:mm',
  time: 'HH:mm',
};

const POPUP_WIDTH = 288; // w-72

const parseValue = (mode, str) => {
  if (!str) return null;
  const dt = DateTime.fromFormat(str, FORMATS[mode]);
  return dt.isValid ? dt : null;
};

const pad2 = (n) => String(n).padStart(2, '0');

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const BASE_MINUTES = Array.from({ length: 12 }, (_, i) => pad2(i * 5));

const DateTimeInput = ({
  mode = 'date',
  value = '',
  onChange,
  min,
  max,
  className,
  disabled = false,
  placeholder,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) => {
  const [draft, setDraft] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const [viewMonth, setViewMonth] = useState(() => {
    const dt = parseValue(mode, value);
    return (dt || DateTime.now()).startOf('month');
  });
  const anchorRef = useRef(null);
  const popupRef = useRef(null);

  const hasCalendar = mode === 'date' || mode === 'datetime';
  const hasTime = mode === 'datetime' || mode === 'time';

  // Keep the typed draft in sync with the committed value.
  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  const emit = useCallback(
    (v) => {
      if (onChange) onChange({ target: { value: v } });
    },
    [onChange]
  );

  const selected = parseValue(mode, value);
  const minDt = min ? parseValue(mode, min) : null;
  const maxDt = max ? parseValue(mode, max) : null;

  // ---- popup positioning (portal + fixed + viewport clamp, same approach
  // as RefreshButton's dropdown so ancestor overflow/z-index can't clip it) --
  const updatePopupPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const estHeight =
      mode === 'time' ? 170 : mode === 'datetime' ? 420 : 370;
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - POPUP_WIDTH - 8)
    );
    let top = rect.bottom + 4;
    if (top + estHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estHeight - 4);
    }
    setPopupPos({ top, left });
  }, [mode]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        anchorRef.current && !anchorRef.current.contains(event.target) &&
        popupRef.current && !popupRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', updatePopupPosition, true);
    window.addEventListener('resize', updatePopupPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', updatePopupPosition, true);
      window.removeEventListener('resize', updatePopupPosition);
    };
  }, [isOpen, updatePopupPosition]);

  const togglePopup = () => {
    if (disabled) return;
    if (!isOpen) {
      const dt = parseValue(mode, value);
      setViewMonth((dt || DateTime.now()).startOf('month'));
      updatePopupPosition();
    }
    setIsOpen((v) => !v);
  };

  // ---- typing ------------------------------------------------------------
  const handleInputChange = (e) => {
    const text = e.target.value;
    setDraft(text);
    if (text === '') {
      emit('');
      return;
    }
    // Only propagate once the text is a complete, valid value for the mode.
    if (parseValue(mode, text)) emit(text);
  };

  const handleBlur = () => {
    // Normalize: an invalid/partial draft reverts to the last valid value.
    if (draft !== '' && !parseValue(mode, draft)) {
      setDraft(value || '');
    }
  };

  // ---- calendar helpers --------------------------------------------------
  const timePartOf = (dt) => (dt ? dt.toFormat('HH:mm') : '00:00');

  const isDayDisabled = (day) => {
    if (minDt && day.startOf('day') < minDt.startOf('day')) return true;
    if (maxDt && day.startOf('day') > maxDt.startOf('day')) return true;
    return false;
  };

  const pickDay = (day) => {
    if (mode === 'date') {
      emit(day.toFormat('yyyy-MM-dd'));
      setIsOpen(false);
    } else {
      // datetime: set the date part, keep the popup open for the time row.
      emit(`${day.toFormat('yyyy-MM-dd')}T${timePartOf(selected)}`);
    }
    setViewMonth(day.startOf('month'));
  };

  const setTime = (hour, minute) => {
    if (mode === 'time') {
      emit(`${hour}:${minute}`);
    } else {
      const datePart = selected
        ? selected.toFormat('yyyy-MM-dd')
        : DateTime.now().toFormat('yyyy-MM-dd');
      emit(`${datePart}T${hour}:${minute}`);
    }
  };

  const handleClear = () => {
    emit('');
    setIsOpen(false);
  };

  const handleTodayNow = () => {
    const now = DateTime.now();
    if (mode === 'date') {
      emit(now.toFormat('yyyy-MM-dd'));
      setIsOpen(false);
    } else if (mode === 'datetime') {
      emit(now.toFormat(FORMATS.datetime));
    } else {
      emit(now.toFormat('HH:mm'));
    }
    setViewMonth(now.startOf('month'));
  };

  // ---- render pieces -----------------------------------------------------
  const selHour = selected ? pad2(selected.hour) : '00';
  const selMinute = selected ? pad2(selected.minute) : '00';
  // Minutes on a 5-step grid, but keep an off-grid current minute selectable.
  const minutes = BASE_MINUTES.includes(selMinute)
    ? BASE_MINUTES
    : [...BASE_MINUTES, selMinute].sort();

  const renderCalendar = () => {
    const firstOfMonth = viewMonth.startOf('month');
    // Sunday-first grid: luxon weekday is Mon=1..Sun=7.
    const gridStart = firstOfMonth.minus({ days: firstOfMonth.weekday % 7 });
    const today = DateTime.now().startOf('day');

    const weeks = [];
    for (let w = 0; w < 6; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        days.push(gridStart.plus({ days: w * 7 + d }));
      }
      weeks.push(days);
    }

    return (
      <div className="p-3">
        {/* Month header */}
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => setViewMonth(viewMonth.minus({ months: 1 }))}
            className="p-2 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50"
            aria-label="Previous month"
          >
            <FaChevronLeft className="w-3 h-3" />
          </button>
          <div className="text-sm font-semibold text-gray-900">
            {viewMonth.toFormat('MMMM yyyy')}
          </div>
          <button
            type="button"
            onClick={() => setViewMonth(viewMonth.plus({ months: 1 }))}
            className="p-2 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50"
            aria-label="Next month"
          >
            <FaChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Weekday row */}
        <div className="grid grid-cols-7 mb-1">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
            <div
              key={d}
              className="w-9 h-6 flex items-center justify-center text-[11px] font-semibold text-gray-400"
            >
              {d}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7">
              {week.map((day) => {
                const inMonth = day.month === viewMonth.month;
                const isSelected =
                  selected && day.hasSame(selected, 'day');
                const isToday = day.hasSame(today, 'day');
                const dayDisabled = isDayDisabled(day);

                let cls =
                  'w-9 h-9 rounded-lg text-sm flex items-center justify-center';
                if (isSelected) {
                  cls += ' bg-cloudza-600 text-white font-semibold';
                } else if (dayDisabled) {
                  cls += ' text-gray-200 cursor-not-allowed';
                } else if (!inMonth) {
                  cls += ' text-gray-300 hover:bg-cloudza-50';
                } else if (isToday) {
                  cls +=
                    ' border border-cloudza-300 text-cloudza-700 hover:bg-cloudza-50';
                } else {
                  cls += ' text-gray-700 hover:bg-cloudza-50';
                }

                return (
                  <button
                    key={day.toISODate()}
                    type="button"
                    disabled={dayDisabled}
                    onClick={() => pickDay(day)}
                    className={cls}
                  >
                    {day.day}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTimeRow = () => (
    <div
      className={`flex items-center justify-center gap-2 px-3 pb-3 ${
        mode === 'time' ? 'pt-3' : 'pt-1'
      }`}
    >
      <SelectField
        portal={false}
        value={selHour}
        onChange={(e) => setTime(e.target.value, selMinute)}
        className="select-field !text-sm"
        aria-label="Hours"
      >
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </SelectField>
      <span className="text-sm font-semibold text-gray-500">:</span>
      <SelectField
        portal={false}
        value={selMinute}
        onChange={(e) => setTime(selHour, e.target.value)}
        className="select-field !text-sm"
        aria-label="Minutes"
      >
        {minutes.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </SelectField>
    </div>
  );

  // Mirror layout-affecting utility classes from the input's className onto
  // the wrapper so the field participates in flex/grid layouts as the bare
  // native input did.
  const inputClassName = className || 'input-field w-full';
  const wrapperLayout = ['w-full', 'flex-1', 'min-w-0']
    .filter((c) => inputClassName.split(/\s+/).includes(c))
    .join(' ');

  return (
    <div className={`relative ${wrapperLayout}`} ref={anchorRef}>
      <input
        type="text"
        value={draft}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsOpen(false);
        }}
        disabled={disabled}
        placeholder={placeholder || PLACEHOLDERS[mode]}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        autoComplete="off"
        className={`${inputClassName} pr-9`}
      />
      <button
        type="button"
        onClick={togglePopup}
        disabled={disabled}
        tabIndex={-1}
        aria-label={hasCalendar ? 'Open calendar' : 'Open time picker'}
        aria-expanded={isOpen}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cloudza-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {hasCalendar ? (
          <FaRegCalendarAlt className="w-4 h-4" />
        ) : (
          <FaRegClock className="w-4 h-4" />
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="popup-surface fixed z-[110] w-72 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
            style={{ top: popupPos.top, left: popupPos.left }}
          >
            {hasCalendar && renderCalendar()}
            {hasTime && renderTimeRow()}

            {/* Footer */}
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleClear}
                className="text-xs font-semibold text-red-700 hover:underline"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleTodayNow}
                className="text-xs font-semibold text-cloudza-700 hover:underline"
              >
                {mode === 'date' ? 'Today' : 'Now'}
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DateTimeInput;
